import {CommonModule} from '@angular/common';
import {Component, Inject, Input} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {EmTranslatePipe, TranslationService, validators} from '@emma-labs/shared/angular';
import {
  CLIENT_AGENT_AFFILIATION_TIME_OPTIONS,
  ClientAgentAffiliationTime,
  ENVIRONMENT,
  EnvironmentBroker,
  IdDocument,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {ToggleButtonModule} from 'primeng/togglebutton';
import {distinctUntilChanged} from 'rxjs/operators';
import {Disclosures} from '../../../entities/proposal-page-object/disclosures';
import {BaseSectionComponent} from '../../base-section/base-section.component';

interface DisclosuresForm {
  clientAgentAffiliationTime: FormControl<ClientAgentAffiliationTime | null>;
  isAccepted: FormControl<boolean | null>;
}

@UntilDestroy()
@Component({
  selector: 'em-disclosures-section',
  templateUrl: './disclosures-section.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    DropdownModule,
    ToggleButtonModule,
    EmTranslatePipe,
  ],
})
export class DisclosuresSectionComponent extends BaseSectionComponent<
  FormGroup<DisclosuresForm>,
  Disclosures
> {
  readonly clientAgentAffiliationTimeOptions = CLIENT_AGENT_AFFILIATION_TIME_OPTIONS;

  @Input({required: true}) primaryAgentFullName!: string;
  @Input({required: true}) clientFullName!: string;
  @Input({required: true}) idDocument!: IdDocument;

  constructor(
    readonly translate: TranslationService,
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();
  }

  @Input({required: true})
  override set props(disclosures: Disclosures | undefined) {
    if (disclosures) {
      super.props = disclosures;
      this.isPropsReceived = false; // The form needs to be updated at every emission to detect updates that might turn off the confirmation toggle-button
    }
  }

  private createForm(): FormGroup<DisclosuresForm> {
    const form = this.fb.group<DisclosuresForm>({
      clientAgentAffiliationTime: this.fb.control(null, Validators.required),
      isAccepted: this.fb.control(null, [Validators.required, validators.isTrue]),
    });

    form.controls.clientAgentAffiliationTime.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => form.controls.isAccepted.patchValue(false));

    form.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((_) => this.propsChange.emit({output: this.formToOutput()}));

    return form;
  }
}
